import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import {
  fetchButtonActions as fetchButtonActionsGobodo,
  fetchButtonAction,
  createButtonAction as createButtonActionGobodo,
  updateButtonAction as updateButtonActionGobodo,
  deleteButtonAction as deleteButtonActionGobodo,
} from '../gobodo/gobodoButtonActionService';
import { mockObjects } from './objectQueries';

// ============================================
// TYPES
// ============================================

export type ButtonType = 'button' | 'quick-action';
export type ButtonLocation = 'detail-header' | 'detail-section' | 'detail-footer' | 'list-row' | 'list-bulk';
export type ActionType = 'call-api' | 'run-workflow' | 'update-fields' | 'navigate' | 'javascript' | 'open-modal';
export type ModalType = 'send-email' | 'log-call' | 'log-meeting' | 'create-task' | 'add-note';

export interface VisibilityRule {
  id: string;
  fieldId: string;
  fieldLabel: string;
  operator: 'equals' | 'notEquals' | 'contains' | 'isBlank' | 'isNotBlank';
  value?: string | number | boolean;
}

export interface ActionConfig {
  type: ActionType;

  // Call API action config
  apiUrl?: string;
  apiMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  apiHeaders?: Record<string, string>;
  apiBody?: string; // JSON string

  // Run Workflow action config
  workflowId?: string;
  workflowName?: string;

  // Update Fields action config
  fieldUpdates?: Array<{
    fieldId: string;
    fieldLabel: string;
    value: string | number | boolean;
  }>;

  // Navigate action config
  navigateUrl?: string;
  openInNewTab?: boolean;

  // JavaScript action config
  javascriptCode?: string;

  // Open Modal action config
  modalType?: ModalType;
}

export interface ButtonAction {
  id: string;
  name: string;
  objectName: string;
  type: ButtonType;
  location: ButtonLocation;
  label: string;
  icon?: string;
  color?: 'purple' | 'blue' | 'green' | 'red' | 'yellow' | 'gray';
  action: ActionConfig;
  successMessage: string;
  errorMessage: string;
  visibilityRules: VisibilityRule[];
  visibilityLogic?: 'AND' | 'OR';
  isActive: boolean;
  createdBy: string;
  createdDate: string;
  lastModified: string;
  lastModifiedBy: string;
}

// ============================================
// MOCK DATA
// ============================================

const mockButtonActions: ButtonAction[] = [];

// Generate some sample button actions for each object
mockObjects.forEach((object, index) => {
  // Sample button 1: Open Send Email Modal
  mockButtonActions.push({
    id: `btn_${object.id}_001`,
    name: `Send ${object.label} Email`,
    objectName: object.apiName,
    type: 'button',
    location: 'detail-header',
    label: `Send Email`,
    icon: 'Mail',
    color: 'blue',
    action: {
      type: 'open-modal',
      modalType: 'send-email'
    },
    successMessage: 'Email sent successfully',
    errorMessage: 'Failed to send email',
    visibilityRules: [],
    isActive: true,
    createdBy: 'System',
    createdDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Admin'
  });

  // Sample button 2: Update Fields
  if (index % 2 === 0) {
    mockButtonActions.push({
      id: `btn_${object.id}_002`,
      name: `Mark ${object.label} as Priority`,
      objectName: object.apiName,
      type: 'quick-action',
      location: 'list-row',
      label: 'Mark Priority',
      icon: 'Star',
      color: 'yellow',
      action: {
        type: 'update-fields',
        fieldUpdates: [
          {
            fieldId: 'priority',
            fieldLabel: 'Priority',
            value: 'High'
          },
          {
            fieldId: 'flagged',
            fieldLabel: 'Flagged',
            value: true
          }
        ]
      },
      successMessage: 'Record marked as priority',
      errorMessage: 'Failed to update record',
      visibilityRules: [
        {
          id: 'rule_001',
          fieldId: 'status',
          fieldLabel: 'Status',
          operator: 'notEquals',
          value: 'Closed'
        }
      ],
      visibilityLogic: 'AND',
      isActive: true,
      createdBy: 'Admin',
      createdDate: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
      lastModified: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      lastModifiedBy: 'Admin'
    });
  }

  // Sample button 3: Navigate
  if (index % 3 === 0) {
    mockButtonActions.push({
      id: `btn_${object.id}_003`,
      name: `View ${object.label} Report`,
      objectName: object.apiName,
      type: 'button',
      location: 'detail-section',
      label: 'View Report',
      icon: 'BarChart',
      color: 'purple',
      action: {
        type: 'navigate',
        navigateUrl: `/reports/${object.apiName.toLowerCase()}`,
        openInNewTab: true
      },
      successMessage: 'Opening report...',
      errorMessage: 'Failed to open report',
      visibilityRules: [],
      isActive: true,
      createdBy: 'Admin',
      createdDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      lastModified: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      lastModifiedBy: 'Admin'
    });
  }
});

// Add a few inactive buttons for testing
mockButtonActions.push({
  id: 'btn_inactive_001',
  name: 'Archive Old Records',
  objectName: 'Client__c',
  type: 'quick-action',
  location: 'list-bulk',
  label: 'Archive Selected',
  icon: 'Archive',
  color: 'gray',
  action: {
    type: 'run-workflow',
    workflowId: 'wf_archive_001',
    workflowName: 'Archive Records Workflow'
  },
  successMessage: 'Records archived successfully',
  errorMessage: 'Failed to archive records',
  visibilityRules: [],
  isActive: false,
  createdBy: 'Admin',
  createdDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
  lastModified: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
  lastModifiedBy: 'Admin'
});

// ============================================
// QUERY KEYS
// ============================================

export const buttonActionKeys = {
  all: ['button-actions'] as const,
  byObject: (objectName: string) => [...buttonActionKeys.all, 'object', objectName] as const,
  detail: (buttonId: string) => [...buttonActionKeys.all, 'detail', buttonId] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all button actions for an object
 */
export function useButtonActions(objectName: string, filters?: {
  type?: ButtonType;
  location?: ButtonLocation;
  isActive?: boolean;
}) {
  return useQuery({
    queryKey: buttonActionKeys.byObject(objectName),
    queryFn: async () => {
      let list: ButtonAction[];
      if (USE_GOBODO_API) {
        list = await fetchButtonActionsGobodo(objectName);
      } else {
        await new Promise(resolve => setTimeout(resolve, 300));
        list = mockButtonActions.filter(btn => btn.objectName === objectName);
      }
      if (filters?.type) list = list.filter(btn => btn.type === filters.type);
      if (filters?.location) list = list.filter(btn => btn.location === filters.location);
      if (filters?.isActive !== undefined) list = list.filter(btn => btn.isActive === filters.isActive);
      return list.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!objectName,
  });
}

/**
 * Get single button action by ID
 */
export function useButtonAction(buttonId: string) {
  return useQuery({
    queryKey: buttonActionKeys.detail(buttonId),
    queryFn: async () => {
      if (USE_GOBODO_API) {
        return fetchButtonAction(buttonId);
      }
      await new Promise(resolve => setTimeout(resolve, 200));
      const button = mockButtonActions.find(btn => btn.id === buttonId);
      if (!button) throw new Error('Button action not found');
      return button;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!buttonId,
  });
}

/**
 * Create new button action
 */
export function useCreateButtonAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (buttonData: Omit<ButtonAction, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'lastModifiedBy'>) => {
      if (USE_GOBODO_API) {
        return createButtonActionGobodo(buttonData);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const newButton: ButtonAction = {
        ...buttonData,
        id: `btn_${Date.now()}`,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User',
      };
      mockButtonActions.push(newButton);
      return newButton;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: buttonActionKeys.byObject(data.objectName) });
    },
  });
}

/**
 * Update button action
 */
export function useUpdateButtonAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ buttonId, updates }: { buttonId: string; updates: Partial<ButtonAction> }) => {
      if (USE_GOBODO_API) {
        return updateButtonActionGobodo(buttonId, updates);
      }
      await new Promise(resolve => setTimeout(resolve, 400));
      const index = mockButtonActions.findIndex(btn => btn.id === buttonId);
      if (index === -1) throw new Error('Button action not found');
      mockButtonActions[index] = {
        ...mockButtonActions[index],
        ...updates,
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User',
      };
      return mockButtonActions[index];
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: buttonActionKeys.byObject(data.objectName) });
      queryClient.invalidateQueries({ queryKey: buttonActionKeys.detail(data.id) });
    },
  });
}

/**
 * Delete button action
 */
export function useDeleteButtonAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ buttonId, objectName }: { buttonId: string; objectName: string }) => {
      if (USE_GOBODO_API) {
        await deleteButtonActionGobodo(buttonId);
        return { buttonId, objectName };
      }
      await new Promise(resolve => setTimeout(resolve, 400));
      const index = mockButtonActions.findIndex(btn => btn.id === buttonId);
      if (index === -1) throw new Error('Button action not found');
      mockButtonActions.splice(index, 1);
      return { buttonId, objectName };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: buttonActionKeys.byObject(data.objectName) });
    },
  });
}

/**
 * Clone button action
 */
export function useCloneButtonAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (buttonId: string) => {
      if (USE_GOBODO_API) {
        const original = await fetchButtonAction(buttonId);
        const { id: _id, createdBy: _cb, createdDate: _cd, lastModified: _lm, lastModifiedBy: _lmb, ...rest } = original;
        return createButtonActionGobodo({ ...rest, name: `${original.name} (Copy)`, label: `${original.label} (Copy)`, isActive: false });
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const original = mockButtonActions.find(btn => btn.id === buttonId);
      if (!original) throw new Error('Button action not found');
      const cloned: ButtonAction = {
        ...original,
        id: `btn_${Date.now()}`,
        name: `${original.name} (Copy)`,
        label: `${original.label} (Copy)`,
        isActive: false,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User',
      };
      mockButtonActions.push(cloned);
      return cloned;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: buttonActionKeys.byObject(data.objectName) });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get human-readable label for button type
 */
export function getButtonTypeLabel(type: ButtonType): string {
  switch (type) {
    case 'button': return 'Custom Button';
    case 'quick-action': return 'Quick Action';
    default: return type;
  }
}

/**
 * Get human-readable label for button location
 */
export function getLocationLabel(location: ButtonLocation): string {
  switch (location) {
    case 'detail-header': return 'Detail Page Header';
    case 'detail-section': return 'Detail Page Section';
    case 'detail-footer': return 'Detail Page Footer';
    case 'list-row': return 'List View Row';
    case 'list-bulk': return 'List View Bulk Actions';
    default: return location;
  }
}

/**
 * Get human-readable label for action type
 */
export function getActionTypeLabel(type: ActionType): string {
  switch (type) {
    case 'call-api': return 'Call API';
    case 'run-workflow': return 'Run Workflow';
    case 'update-fields': return 'Update Fields';
    case 'navigate': return 'Navigate to URL';
    case 'javascript': return 'Run JavaScript';
    default: return type;
  }
}

/**
 * Count buttons by location for an object
 */
export function countButtonsByLocation(buttons: ButtonAction[], location: ButtonLocation): number {
  return buttons.filter(btn => btn.location === location && btn.isActive).length;
}

/**
 * Validate visibility rules
 */
export function validateVisibilityRules(rules: VisibilityRule[]): { valid: boolean; error?: string } {
  if (rules.length === 0) return { valid: true };

  for (const rule of rules) {
    if (!rule.fieldId || !rule.fieldLabel) {
      return { valid: false, error: 'Each visibility rule must have a field selected' };
    }

    const needsValue = !['isBlank', 'isNotBlank'].includes(rule.operator);
    if (needsValue && rule.value === undefined) {
      return { valid: false, error: `Visibility rule for "${rule.fieldLabel}" requires a value` };
    }
  }

  return { valid: true };
}
